import { Section } from "views/applications-id-report/components/Section";
import { toMoney } from "shared/utils";
import { DateTime } from "luxon";
import { Hide, Show } from "shared/components";
import { NetIncomeChart } from "./NetIncomeGraphSlice";
import { ApplicationStatus } from "shared/types";
import { mapNetIncomeAnalysis } from "../helpers";
import { ExpandableTable } from "../components/UniversalTable";
import { AdjustedApplicationRentActivityLog } from "views/applications-id-report/components/AdjustedApplicationRentIncomeSection";
import { IncomeSlice } from "./TablesSlice/IncomeSlice";
import { DocumentWarnings } from "./DocumentWarningsSlice";
import { PrintReady } from "views/applications-id-report/providers/PrintProvider";
import { getAdditionalDataPerVerificationType } from "../utils";
import { NoIncomeSection } from "../components/NoIncomeSection";
import { NetIncomeAnalysisExpandedRow } from "../components/NetIncomeAnalysisExpandedRow";
import IncomeSummary from "../components/IncomeSummary";
import { DebtDetails } from "../components/DebtDetails";
import {
  INCOMPLETE_APPLICATION_STATUSES,
  INCOMPLETE_VERIFICATION_STATUSES
} from "../constants";
import {
  getIncomeReverifyRequest,
  getLatestReverifyRequestForIncome
} from "./ReverifySlice/utils";
import { useCombinedIncomeContext } from "../providers/useCombinedIncome";
import { ReportFallbackSlice } from "./ReportFallbackSlice";

export const ReportContentSlice = () => (
  <ReportFallbackSlice>
    <ReportBody />
  </ReportFallbackSlice>
);

const ReportBody = () => {
  const {
    applicationMeta,
    currentlySelectedVerificationId,
    verificationData: reportData,
    verificationReportData: data,
    incomes,
    bankStatements,
    pendingReverifyRequests,
    completedReverifyRequests,
    failedReverifyRequests,
    verificationStatus,
    applicationRentActivityLogsMeta,
    hideProjectedDebtSection
  } = useCombinedIncomeContext();

  if (!data || !reportData || !applicationMeta.data) return null;

  const noIncome = data.incomes.find((income) => income.type === "no_income");
  const summary = data.total_summary;
  const netIncomeData = data.net_income_analysis;

  const isVerificationIncomplete =
    INCOMPLETE_VERIFICATION_STATUSES.includes(verificationStatus) &&
    INCOMPLETE_APPLICATION_STATUSES.includes(
      applicationMeta.data.status as ApplicationStatus
    );

  if (noIncome) {
    const housingAssistance = data.incomes.find(
      (income) => income.type === "housing_assistance"
    );

    return (
      <NoIncomeSection
        noIncome={noIncome}
        housingAssistance={housingAssistance}
      />
    );
  }

  return (
    <div>
      <Show if={currentlySelectedVerificationId}>
        <PrintReady verificationId={currentlySelectedVerificationId!} />
      </Show>

      <Section
        sectionTitle="Income summary"
        maxWidth="100%"
        sectionClassName="p-0"
      >
        <IncomeSummary />
      </Section>

      <DocumentWarnings />

      <Hide if={isVerificationIncomplete}>
        <Section sectionTitle="Net income analysis" maxWidth="1100px">
          <NetIncomeChart data={netIncomeData} />

          <div className="mt-[60px]">
            <ExpandableTable
              hideHeader
              rowClassName="items-center pr-4"
              headerRowClassName="text-content-1000 text-sm font-medium"
              columns={[
                {
                  header: "month",
                  accessor: (row) =>
                    DateTime.fromISO(row.month).toFormat("MMMM, yyyy"),
                  widthClass: "w-full"
                },
                {
                  header: "total",
                  accessor: (row) => toMoney(row.total),
                  widthClass: "w-[120px] shrink-0"
                }
              ]}
              rows={mapNetIncomeAnalysis(netIncomeData)}
              renderExpanded={(original) => (
                <NetIncomeAnalysisExpandedRow original={original} />
              )}
            />
          </div>
        </Section>
      </Hide>

      {incomes.map((income, index) => (
        <IncomeSlice
          key={index}
          income={income}
          additionalData={getAdditionalDataPerVerificationType(
            data,
            income,
            bankStatements
          )}
          pendingReverifyRequest={getIncomeReverifyRequest(
            income.id,
            pendingReverifyRequests
          )}
          completedReverifyRequest={getLatestReverifyRequestForIncome(
            income.id,
            completedReverifyRequests
          )}
          failedReverifyRequest={getLatestReverifyRequestForIncome(
            income.id,
            failedReverifyRequests
          )}
        />
      ))}

      <Hide
        if={
          hideProjectedDebtSection ||
          Boolean(
            !summary.debt_gross_income_ratio &&
            !summary.debt_net_income_ratio &&
            !summary.debt_self_attested_gross_income_ratio
          )
        }
      >
        <DebtDetails
          summary={summary}
          isVerificationIncomplete={isVerificationIncomplete}
        />
      </Hide>

      <div className="space-y-8 p-6 pt-0 text-sm">
        {applicationRentActivityLogsMeta.data?.map((activityItem, index) => (
          <AdjustedApplicationRentActivityLog
            key={index}
            activityItem={activityItem}
          />
        ))}
      </div>
    </div>
  );
};
